import {
  HabitSchedule,
  ScheduleEntryState,
  ScheduleTargetEntry,
  Timestamp,
} from '@griply/types';
import { addWeeks, eachDayOfInterval } from 'date-fns';

import { getTimeframeOccurrences } from './get-timeframe-occurrences';
import { isBetween } from './lib/date-fns';
import { getResolvedScheduleDays } from './schedules/get-resolved-schedule-days';
import { normalizeSchedules } from './schedules/normalize-schedules';

export type GetTimeframeDayEntriesOptions = {
  startDate: Timestamp;
  endDate: Timestamp;
};

export type TimeframeDayEntries = {
  /** The padded range — the month view renders entries either side of the timeframe. */
  dateEntries: ScheduleTargetEntry[];
  /** Only the ones inside the asked-for range. */
  timeframeDateEntries: ScheduleTargetEntry[];
};

const PADDING_WEEKS = 5;

// One entry per day over the padded range: what the habit's schedules say happened on that day.
// Schedules are normalized first: `getResolvedScheduleDays` caps a schedule at its successor's start only
// when that successor has one, so two open-ended schedules would both start at the epoch and the later one
// would own every day in the window (anti-pattern #11).
export const getTimeframeDayEntries = (
  schedules: HabitSchedule[],
  { startDate, endDate }: GetTimeframeDayEntriesOptions,
): TimeframeDayEntries => {
  const rangeStartDate = addWeeks(startDate, -PADDING_WEEKS);
  const rangeEndDate = addWeeks(endDate, PADDING_WEEKS);

  const normalizedSchedules = normalizeSchedules(schedules);
  const { occurrences } = getTimeframeOccurrences(normalizedSchedules, {
    startDate: rangeStartDate,
    endDate: rangeEndDate,
  });

  const targetEntries = getResolvedScheduleDays({
    schedules: normalizedSchedules,
    days: eachDayOfInterval({ start: rangeStartDate, end: rangeEndDate }),
    occurrences,
    fallbackEndDate: rangeEndDate,
  }).map<ScheduleTargetEntry>((day) => ({
    date: day.date,
    id: day.entryId ?? `${day.date.getTime()}`,
    state: day.state ?? ScheduleEntryState.Complete,
    value: day.value,
    hidden: day.hidden ?? undefined,
    target: day.target,
    onDateManual: day.onDateManual,
    showValueAsBadge: (day.value ?? 0) > 1,
  }));

  return {
    dateEntries: targetEntries.filter(({ date }) =>
      isBetween(date, rangeStartDate, rangeEndDate),
    ),
    timeframeDateEntries: targetEntries.filter(({ date }) =>
      isBetween(date, startDate, endDate),
    ),
  };
};
